// components/modals/ExpenseDetailsModal.js
import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Slide from '@mui/material/Slide';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import CategoryIcon from '@mui/icons-material/Category';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HomeIcon from '@mui/icons-material/Home';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

// Modal transition effect
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Styled components
const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(2, 3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
}));

const DetailCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2.5),
    height: '100%',
    borderRadius: theme.spacing(1),
    boxShadow: 'none',
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.grey[50],
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    },
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    fontWeight: 500,
}));

const DetailValue = styled(Typography)(({ theme }) => ({
    fontWeight: 500,
    color: theme.palette.text.primary,
}));

const ExpenseDetailsModal = ({ open, onClose, expense }) => {
    if (!expense) return null;

    const formatCurrency = (value) => {
        return parseFloat(value).toLocaleString('en-US', {
            style: 'currency',
            currency: 'KSH',
        });
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            maxWidth="md"
            fullWidth
            onClose={onClose}
            aria-describedby="expense-details-dialog"
            PaperProps={{
                elevation: 3,
                sx: { borderRadius: 2, overflow: 'hidden' }
            }}
        >
            <StyledDialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <ReceiptLongIcon />
                    <Typography variant="h6">Expense Details</Typography>
                </Box>
                <Button
                    variant="text"
                    color="inherit"
                    onClick={onClose}
                    sx={{
                        minWidth: 'auto',
                        p: 0.5,
                        color: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': {
                            color: 'white',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                    }}
                >
                    <CloseIcon />
                </Button>
            </StyledDialogTitle>

            <DialogContent dividers sx={{ p: 3 }}>
                <Grid container spacing={3}>
                    {/* Description */}
                    <Grid item xs={12}>
                        <DetailCard sx={{
                            p: 3,
                            backgroundColor: 'primary.light',
                            color: 'primary.contrastText',
                            borderColor: 'primary.main'
                        }}>
                            <DetailLabel sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                                <DescriptionIcon sx={{ opacity: 0.9 }} />
                                Description
                            </DetailLabel>
                            <Typography variant="h6" sx={{ fontWeight: 500, mt: 1 }}>
                                {expense.description}
                            </Typography>
                        </DetailCard>
                    </Grid>

                    {/* Amount */}
                    <Grid item xs={12} md={6}>
                        <DetailCard sx={{ borderColor: 'success.light' }}>
                            <DetailLabel>
                                <AttachMoneyIcon fontSize="small" color="success" />
                                Amount
                            </DetailLabel>
                            <DetailValue variant="h4" color="success.main" sx={{ mt: 1.5, mb: 0.5 }}>
                                {formatCurrency(expense.cost)}
                            </DetailValue>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ mt: 'auto' }}>
                                <Typography variant="caption" color="text.secondary">
                                    Recorded on {expense.date_created}
                                </Typography>
                            </Box>
                        </DetailCard>
                    </Grid>

                    {/* Property Info */}
                    <Grid item xs={12} md={6}>
                        <DetailCard>
                            <DetailLabel>
                                <HomeWorkIcon fontSize="small" color="primary" />
                                Property
                            </DetailLabel>
                            <DetailValue variant="h6" sx={{ mb: 2 }}>
                                {expense.property_name}
                            </DetailValue>

                            <DetailLabel sx={{ mt: 2 }}>
                                <HomeIcon fontSize="small" color="primary" />
                                House Number
                            </DetailLabel>
                            <DetailValue variant="h6">
                                {expense.house_number && expense.house_number !== '' ? (
                                    <Chip
                                        label={expense.house_number}
                                        color="primary"
                                        variant="outlined"
                                        sx={{
                                            fontWeight: 600,
                                            minWidth: '80px',
                                            height: '32px',
                                            borderRadius: '8px'
                                        }}
                                    />
                                ) : (
                                    <Typography color="text.secondary">— Not specified —</Typography>
                                )}
                            </DetailValue>
                        </DetailCard>
                    </Grid>

                    {/* Expense Type */}
                    <Grid item xs={12} sm={6}>
                        <DetailCard>
                            <DetailLabel>
                                <CategoryIcon fontSize="small" color="primary" />
                                Expense Type
                            </DetailLabel>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
                                <Chip
                                    icon={<CategoryIcon />}
                                    label={expense.expense_type_name}
                                    color="default"
                                    sx={{
                                        bgcolor: 'background.paper',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        height: '36px',
                                        px: 1
                                    }}
                                />
                            </Box>
                        </DetailCard>
                    </Grid>

                    {/* Date */}
                    <Grid item xs={12} sm={6}>
                        <DetailCard>
                            <DetailLabel>
                                <CalendarMonthIcon fontSize="small" color="primary" />
                                Date
                            </DetailLabel>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 0.5,
                                mt: 1.5
                            }}>
                                <Typography variant="h6">
                                    {expense.date_created}
                                </Typography>
                                <Chip
                                    size="small"
                                    label="Date Created"
                                    variant="outlined"
                                    color="primary"
                                    sx={{ height: '24px', fontSize: '0.75rem' }}
                                />
                            </Box>
                        </DetailCard>
                    </Grid>

                    {/* Additional Notes or Information (if available) */}
                    {expense.notes && (
                        <Grid item xs={12}>
                            <DetailCard>
                                <DetailLabel>
                                    <DescriptionIcon fontSize="small" color="primary" />
                                    Additional Notes
                                </DetailLabel>
                                <Typography variant="body1" sx={{ mt: 1 }}>
                                    {expense.notes}
                                </Typography>
                            </DetailCard>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onClose}
                    startIcon={<CloseIcon />}
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        px: 3
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

ExpenseDetailsModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    expense: PropTypes.object,
};

export default ExpenseDetailsModal;